import { Suspense, lazy, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
  useParams,
} from 'react-router-dom';
import { ScrollBehaviour } from './ScrollBehaviour';
import { CookieBanner } from '../components/CookieBanner';
import { LEGACY_ROOTS, orgSegment, useOrgPath } from './paths';
import { useAuth } from '../features/auth/AuthProvider';
import { hasTotpFactor } from '../features/auth/totp';
import { SignIn } from '../features/auth/SignIn';
import { Landing } from '../features/marketing/Landing';
import { useMyOrgs } from '../features/org/queries';
import { Skeleton } from '../components';

// Every screen behind sign-in, and every public page but the landing, loads
// on first visit to it. The landing page is what a visitor measures, and it
// should not carry the planner, the reports and the invoice editor with it.
const AppShell = lazy(() => import('./AppShell').then((m) => ({ default: m.AppShell })));
const Admin = lazy(() => import('../features/admin/Admin').then((m) => ({ default: m.Admin })));
const Settings = lazy(() => import('../features/org/Settings').then((m) => ({ default: m.Settings })));
const Dashboard = lazy(() => import('../features/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })));
const MyTasks = lazy(() => import('../features/work/MyTasks').then((m) => ({ default: m.MyTasks })));
const NewTask = lazy(() => import('../features/work/NewTask').then((m) => ({ default: m.NewTask })));
const Clients = lazy(() => import('../features/work/Clients').then((m) => ({ default: m.Clients })));
const ClientDetail = lazy(() => import('../features/work/ClientDetail').then((m) => ({ default: m.ClientDetail })));
const Projects = lazy(() => import('../features/work/Projects').then((m) => ({ default: m.Projects })));
const ProjectTaskList = lazy(() => import('../features/work/ProjectTaskList').then((m) => ({ default: m.ProjectTaskList })));
const TaskDetail = lazy(() => import('../features/work/TaskDetail').then((m) => ({ default: m.TaskDetail })));
const TimeTracking = lazy(() => import('../features/time/TimeTracking').then((m) => ({ default: m.TimeTracking })));
const Timesheet = lazy(() => import('../features/time/Timesheet').then((m) => ({ default: m.Timesheet })));
const Planner = lazy(() => import('../features/planner/Planner').then((m) => ({ default: m.Planner })));
const Capacity = lazy(() => import('../features/capacity/Capacity').then((m) => ({ default: m.Capacity })));
const Leave = lazy(() => import('../features/leave/Leave').then((m) => ({ default: m.Leave })));
const Reports = lazy(() => import('../features/reports/Reports').then((m) => ({ default: m.Reports })));
const Notifications = lazy(() => import('../features/notifications/Notifications').then((m) => ({ default: m.Notifications })));
const Trash = lazy(() => import('../features/work/Trash').then((m) => ({ default: m.Trash })));
const TeamHub = lazy(() => import('../features/team/TeamHub').then((m) => ({ default: m.TeamHub })));
const PersonDetail = lazy(() => import('../features/team/PersonDetail').then((m) => ({ default: m.PersonDetail })));
const Finance = lazy(() => import('../features/finance/Finance').then((m) => ({ default: m.Finance })));
const WeekReview = lazy(() => import('../features/time/WeekReview').then((m) => ({ default: m.WeekReview })));
const Goals = lazy(() => import('../features/goals/Goals').then((m) => ({ default: m.Goals })));
const QuoteEditor = lazy(() => import('../features/finance/QuoteEditor').then((m) => ({ default: m.QuoteEditor })));
const InvoiceEditor = lazy(() => import('../features/finance/InvoiceEditor').then((m) => ({ default: m.InvoiceEditor })));
const Features = lazy(() => import('../features/marketing/Features').then((m) => ({ default: m.Features })));
const Privacy = lazy(() => import('../features/marketing/Privacy').then((m) => ({ default: m.Privacy })));
const Refunds = lazy(() => import('../features/marketing/Privacy').then((m) => ({ default: m.Refunds })));
const Cookies = lazy(() => import('../features/marketing/Cookies').then((m) => ({ default: m.Cookies })));
const CreateOrganisation = lazy(() => import('../features/org/CreateOrganisation').then((m) => ({ default: m.CreateOrganisation })));
const AcceptInvite = lazy(() => import('../features/auth/AcceptInvite').then((m) => ({ default: m.AcceptInvite })));
const SignUp = lazy(() => import('../features/auth/SignUp').then((m) => ({ default: m.SignUp })));
const SetPassword = lazy(() => import('../features/auth/SetPassword').then((m) => ({ default: m.SetPassword })));
const VerifyEmail = lazy(() => import('../features/auth/VerifyEmail').then((m) => ({ default: m.VerifyEmail })));
const Everywhere = lazy(() => import('../features/everywhere/Everywhere').then((m) => ({ default: m.Everywhere })));
const RequireTwoFactor = lazy(() => import('../features/auth/RequireTwoFactor').then((m) => ({ default: m.RequireTwoFactor })));


/**
 * Routing.
 *
 * Every application route sits under `/{org-slug}` and behind `RequireOrg`,
 * which waits for auth and for the person's orgs to load before deciding
 * anything. Redirecting on a half-loaded auth state is how people end up
 * bounced to sign-in while already signed in.
 */

/** The bare root: send the person to whichever workspace they were last in. */
/**
 * Between the claims arriving and the active org being derived from them
 * there is one render where the person is signed in, belongs to orgs, and
 * `orgId` is still null. Treating that as "no org" sent every deep link
 * through /create-organisation and out to the dashboard, losing the path.
 */
function orgStillResolving(claims: { orgs: Record<string, unknown> }, orgId: string | null): boolean {
  return !orgId && Object.keys(claims.orgs).length > 0;
}

/**
 * Only send someone to create an organisation on positive evidence that they
 * have none: the profile has loaded and lists no orgs, and the claims agree.
 * A still-empty claim set on its own is what the first render after sign-in
 * looks like, and acting on it throws the URL away.
 */
function provablyNoOrgs(
  profile: { orgIds: string[] } | null,
  claims: { orgs: Record<string, unknown> },
): boolean {
  return !!profile && profile.orgIds.length === 0 && Object.keys(claims.orgs).length === 0;
}

/** The bare root: the landing page for a visitor; the last workspace for a member. */
function OrgLanding() {
  const { status, claims, orgId, profile, user } = useAuth();
  const orgs = useMyOrgs(Object.keys(claims.orgs));

  if (status === 'loading') return <BootSkeleton />;
  if (status === 'signed-out') return <Landing />;
  if (profile?.passwordSetupPending) return <Navigate to="/set-password" replace />;
  if (user && !user.emailVerified) return <Navigate to="/verify-email" replace />;
  if (orgStillResolving(claims, orgId)) return <BootSkeleton />;
  if (!orgId) {
    return provablyNoOrgs(profile, claims) ? <Navigate to="/create-organisation" replace /> : <BootSkeleton />;
  }
  if (orgs.isPending) return <BootSkeleton />;

  const current = orgs.data?.find((org) => org.orgId === orgId);
  // The org doc failing to load is not a reason to strand someone at a blank
  // screen; the id works as a segment and the slug takes over on the next load.
  // `?go=` is what the installed app's shortcuts arrive with.
  const go = new URLSearchParams(window.location.search).get('go');
  const tail = go && /^[a-z-]+$/.test(go) ? `/${go}` : '';
  return <Navigate to={`/${current ? orgSegment(current) : orgId}${tail}`} replace />;
}

function RequireOrg() {
  const { status, claims, orgId, profile, setOrgId, user } = useAuth();
  const { orgSlug } = useParams();
  const location = useLocation();
  const orgs = useMyOrgs(Object.keys(claims.orgs));

  const matched =
    orgs.data?.find((org) => org.slug === orgSlug) ??
    orgs.data?.find((org) => org.orgId === orgSlug) ??
    null;
  // An address the workspace used to have: keep the destination, swap the segment.
  const viaOldSlug = !matched && orgSlug ? (orgs.data?.find((org) => org.previousSlugs?.includes(orgSlug)) ?? null) : null;

  // The URL decides which org is active. Doing this in an effect rather than
  // during render keeps the auth state the single writer of `orgId`.
  useEffect(() => {
    if (matched && matched.orgId !== orgId) setOrgId(matched.orgId);
  }, [matched, orgId, setOrgId]);

  if (status === 'loading') return <BootSkeleton />;
  if (status === 'signed-out') return <Navigate to="/sign-in" replace />;
  // An account an admin set up chooses its own password before anything else.
  if (profile?.passwordSetupPending) {
    return <Navigate to={`/set-password?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  // A self-served account proves its address before anything else. The other
  // doors — invites, admin-created logins — arrive already verified.
  if (user && !user.emailVerified) {
    return <Navigate to={`/verify-email?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (orgStillResolving(claims, orgId)) return <BootSkeleton />;
  if (!orgId) {
    return provablyNoOrgs(profile, claims) ? <Navigate to="/create-organisation" replace /> : <BootSkeleton />;
  }
  if (orgs.isPending) return <BootSkeleton />;

  if (viaOldSlug) {
    const rest = location.pathname.replace(`/${orgSlug}`, '');
    return <Navigate to={`/${orgSegment(viaOldSlug)}${rest}${location.search}`} replace />;
  }

  if (!matched) {
    const current = orgs.data?.find((org) => org.orgId === orgId);
    const segment = current ? orgSegment(current) : orgId;

    // The org document did not load — offline, or a claim that has outrun the
    // membership. The person is still in that org, so let them through rather
    // than redirecting to the address they are already at, which would loop.
    if (orgSlug === segment) return <Outlet />;

    // A link written before workspaces had slugs — `/reports` rather than
    // `/acme/reports`. Keep the destination and add the workspace.
    if (orgSlug && LEGACY_ROOTS.has(orgSlug)) {
      return <Navigate to={`/${segment}${location.pathname}${location.search}`} replace />;
    }
    return <Navigate to={`/${segment}`} replace />;
  }

  // Screens read `orgId` from auth, so rendering them before the switch has
  // landed would fire every query against the previous workspace.
  if (matched.orgId !== orgId) return <BootSkeleton />;

  // A workspace that requires two-factor stops here until the account has
  // it — the same shape as the address proof above, one door later.
  if (matched.security?.requireTotp && user && !hasTotpFactor(user)) {
    return <Navigate to={`/two-factor?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  return <Outlet />;
}

/** An unknown path inside a workspace lands on that workspace's dashboard. */
function OrgRootRedirect() {
  const orgPath = useOrgPath();
  return <Navigate to={orgPath('/')} replace />;
}

/** A skeleton of the shell, so the first paint is the layout rather than a flash. */
function BootSkeleton() {
  return (
    <div className="flex h-full gap-5 bg-canvas p-5">
      <Skeleton className="h-full w-sidebar rounded-card" />
      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <Skeleton className="h-16 w-full rounded-card" />
        <Skeleton className="h-40 w-full rounded-card" />
        <Skeleton className="min-h-0 flex-1 rounded-card" />
      </div>
    </div>
  );
}

/**
 * The platform owner's screen lives outside every workspace: there is one
 * owner for the whole platform, not one per org. Signed-in people reach it;
 * the screen itself gates on the superAdmin claim.
 */
function PlatformShell() {
  const { status } = useAuth();
  if (status === 'loading') return <BootSkeleton />;
  if (status === 'signed-out') return <Navigate to="/sign-in" replace />;
  return (
    <div className="flex h-full flex-col gap-5 bg-canvas p-5">
      <Outlet context={{ openNav: () => undefined }} />
    </div>
  );
}

/**
 * Everything sits under one pathless route, so the scroll behaviour is
 * mounted once for the whole application rather than per page.
 */
function Root() {
  return (
    <>
      <ScrollBehaviour />
      <Outlet />
      <CookieBanner />
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
  { path: '/sign-in', element: <SignIn /> },
  { path: '/sign-up', element: <SignUp /> },
  { path: '/set-password', element: <SetPassword /> },
  { path: '/verify-email', element: <VerifyEmail /> },
  { path: '/two-factor', element: <RequireTwoFactor /> },
  { path: '/admin', element: <PlatformShell />, children: [{ index: true, element: <Admin /> }] },
  { path: '/everywhere', element: <PlatformShell />, children: [{ index: true, element: <Everywhere /> }] },
  { path: '/create-organisation', element: <CreateOrganisation /> },
  { path: '/invite/:inviteId', element: <AcceptInvite /> },
  { path: '/', element: <OrgLanding /> },
  { path: '/features', element: <Features /> },
  { path: '/privacy', element: <Privacy /> },
  { path: '/refunds', element: <Refunds /> },
  { path: '/cookies', element: <Cookies /> },
  {
    path: '/:orgSlug',
    element: <RequireOrg />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Dashboard /> },
          { path: 'my-tasks', element: <MyTasks /> },
          { path: 'projects', element: <Projects /> },
          { path: 'projects/:projectId', element: <ProjectTaskList /> },
          { path: 'clients', element: <Clients /> },
          { path: 'clients/:clientId', element: <ClientDetail /> },
          { path: 'tasks/new', element: <NewTask /> },
          { path: 'tasks/:taskId', element: <TaskDetail /> },
          { path: 'time', element: <TimeTracking /> },
          { path: 'time/timesheet', element: <Timesheet /> },
          { path: 'time/review', element: <WeekReview /> },
          { path: 'planner', element: <Planner /> },
          { path: 'capacity', element: <Capacity /> },
          { path: 'leave', element: <Leave /> },
          { path: 'leave/requests/:requestId', element: <Leave /> },
          { path: 'reports', element: <Reports /> },
          { path: 'notifications', element: <Notifications /> },
          { path: 'trash', element: <Trash /> },
          { path: 'team', element: <TeamHub /> },
          { path: 'team/:uid', element: <PersonDetail /> },
          { path: 'finance', element: <Finance /> },
          { path: 'goals', element: <Goals /> },
          { path: 'finance/invoices/:invoiceId', element: <InvoiceEditor /> },
          { path: 'finance/quotes/:quoteId', element: <QuoteEditor /> },
          { path: 'settings', element: <Settings /> },
          { path: 'settings/profile', element: <Settings /> },
          { path: '*', element: <OrgRootRedirect /> },
        ],
      },
    ],
  },
    ],
  },
]);
